import { observable, action, computed } from 'mobx';
import { setter } from 'mobx-decorators';
import translationsStore from '../translationsStore';
import agent from '../../services/web/agent';
import erorrHandler from '../../services/web/erorrHandler';

class LDAPStore {

    groupMembersAttributeSourceItems = ['DN', 'Username'];
    
    @observable getLDAPServerProgress;
    @observable createLDAPServerProgress;
    @observable createUpdateLDAPServersProgress;
    @observable deleteLDAPServerProgress;
    @observable testLDAPServerProgress;

    @observable getLDAPServerErrorMessage;
    @observable createLDAPServerErrorMessage;
    @observable createLDAPServersErrorMessage;
    @observable updateLDAPServerErrorMessage;
    @observable deleteLDAPServerErrorMessage;
    @observable testLDAPServerMessage;
    @observable testLDAPServerResult;

    // form 1
    @setter @observable active = true;
    @setter @observable name = '';
    @setter @observable host = '';
    @setter @observable port = '';
    @setter @observable username = '';
    @setter @observable password = '';
    @setter @observable useSsl = false;
    @setter @observable verifySSLCertificate = false;

    // form 2
    @setter @observable ldapDirectoryType = this.directoryTypes()[0].id;
    @setter @observable ssoEnabled = false;
    @setter @observable mappedDomainId = -1;
    @setter @observable baseDn = '';
    @setter @observable additionalUserDn = '';
    @setter @observable userObjectClass = '';
    @setter @observable userObjectFilter = '';
    @setter @observable usernameAttribute = '';
    @setter @observable firstNameAttribute = '';
    @setter @observable lastNameAttribute = '';
    @setter @observable emailAttribute = '';

    // form 3
    @setter @observable synchronizationEnabled = false;
    @setter @observable defaultRoleId = -1;
    @setter @observable defaultTeamId = '';
    @setter @observable updateTeamAndRoleUponLoginEnabled = true;
    @setter @observable periodicalSynchronizationEnabled = true;
    @setter @observable advancedTeamAndRoleMappingEnabled = false;
    @setter @observable defaultTeamEnabled = false;
    @setter @observable defaultRoleEnabled = false;
    @setter @observable additionalGroupDn = '';
    @setter @observable groupObjectClass = '';
    @setter @observable groupObjectFilter = '';
    @setter @observable groupNameAttribute = '';
    @setter @observable groupMembersAttribute = '';
    @setter @observable userMembershipAttribute = '';    
	@setter @observable groupMembersAttributeSource = 'DN';

    @action getLDAPServer(serverId) {
        
        this.getLDAPServerProgress = true;

        return agent.LDAPServers.getLDAPServer(serverId)
            .then(action((ldapServer) => {
               
                return ldapServer;
            }))
            .catch(action((err) => {

                this.getLDAPServerErrorMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {

                this.getLDAPServerProgress = false;
            }));
    }

    @action createLDAPServer() {

        this.createUpdateLDAPServersProgress = true;

        return agent.LDAPServers.createLDAPServer(this.getLDAPServerModel())
            .then(action((serverId) => {
                
                return serverId;
            }))
            .catch(action((err) => {

                this.createLDAPServerErrorMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {

                this.createUpdateLDAPServersProgress = false;
            }));

    }

    @action updateLDAPServer(serverId) {

        this.createUpdateLDAPServersProgress = true;

        return agent.LDAPServers.updateLDAPServer(serverId, this.getLDAPServerModel())
            .then(action((servers) => {

                this.resetForm();
            }))
            .catch(action((err) => {

                this.updateLDAPServerErrorMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {

                this.createUpdateLDAPServersProgress = false;
            }));
    }

    @action deleteLDAPServer(serverId) {

        this.deleteLDAPServerProgress = true;

        return agent.LDAPServers.deleteLDAPServer(serverId)
            .catch(action((err) => {

                this.deleteLDAPServerErrorMessage = erorrHandler.getErrorMessage(err);
                
                throw err;
            }))
            .finally(action(() => {

                this.deleteLDAPServerProgress = false;
            }));
    }

    @action testLDAPServerConnection(stepIndex) {

        this.resetTestConnection();
        this.testLDAPServerProgress = true;
        
        return agent.LDAPServers.testLDAPServerConnection(this.getLDAPServerModelByStep(stepIndex))          
            .then(action((testConnectionResult) => {

                this.testLDAPServerResult = testConnectionResult.isSuccessful;
                this.testLDAPServerMessage = testConnectionResult.message;

                return testConnectionResult;
            }))
            .catch(action((err) => {

                this.testLDAPServerResult = false;
                this.testLDAPServerMessage = erorrHandler.getErrorMessage(err);

                throw err;
            }))
            .finally(action(() => {

                this.testLDAPServerProgress = false;
            }));
    }

    @action directoryTypes() {

        return [
            {
                id: '-1',
                text: translationsStore.translate('SELECT_DIRECTORY_TYPE')
            },
            {
                id: 'activedirectory',
                text: 'Active Directory'
            },
            {
                id: 'openldap',
                text: 'Open LDAP'
            },
            {
                id: 'customldapserver',
                text: 'Custom LDAP server'
            }
        ];
    }

    @action resetForm() {

        // form 1
        this.active = true;
        this.name = '';
        this.host = '';
        this.port = '';
        this.username = '';
        this.password = '';
        this.useSsl = false;
        this.verifySSLCertificate = false;

        // form 2
        this.ldapDirectoryType = this.directoryTypes()[0].id;
        this.ssoEnabled = false;
        this.mappedDomainId = -1;
        this.baseDn = '';
        this.additionalUserDn = '';
        this.userObjectClass = '';
        this.userObjectFilter = '';
        this.usernameAttribute = '';
        this.firstNameAttribute = '';
        this.lastNameAttribute = '';
        this.emailAttribute = '';

        // form 3
        this.resetForm3();
    }

    @action resetForm3 = () => {

        this.synchronizationEnabled = false;
        this.defaultRoleId = '';
        this.defaultTeamId = '';
        this.updateTeamAndRoleUponLoginEnabled = true;
        this.periodicalSynchronizationEnabled = true;
        this.advancedTeamAndRoleMappingEnabled = false;
        this.defaultTeamEnabled = false;
        this.defaultRoleEnabled = false;
        this.additionalGroupDn = '';
        this.groupObjectClass = '';
        this.groupObjectFilter = '';
        this.groupNameAttribute = '';
        this.groupMembersAttribute = '';
        this.userMembershipAttribute = '';
    }

    @action resetTestConnection = () => {   
        this.testLDAPServerMessage = null;
    }

    getLDAPServerModel() {

        const roleId = this.defaultRoleId == -1 ? null : this.defaultRoleId;
        const mappedDomainId = this.mappedDomainId == -1 ? null : this.mappedDomainId;

        return {
            active: this.active,           
            name: this.name,
            host: this.host,
            port: this.port,
            username: this.username,
            password: this.password,
            useSsl: this.useSsl,
            verifySslCertificate: this.verifySSLCertificate,          
            ldapDirectoryType: this.ldapDirectoryType,
            ssoEnabled: this.ssoEnabled,
            mappedDomainId: mappedDomainId,
            baseDn: this.baseDn,
            additionalUserDn: this.additionalUserDn,
            userObjectFilter: this.userObjectFilter,
            userObjectClass: this.userObjectClass,
            usernameAttribute: this.usernameAttribute,
            firstNameAttribute: this.firstNameAttribute,
            lastNameAttribute: this.lastNameAttribute,
            emailAttribute: this.emailAttribute,                
            synchronizationEnabled: this.synchronizationEnabled,
            defaultRoleId: roleId,
            defaultTeamId: this.defaultTeamId,
            updateTeamAndRoleUponLoginEnabled: this.updateTeamAndRoleUponLoginEnabled,
            periodicalSynchronizationEnabled: this.periodicalSynchronizationEnabled,
            advancedTeamAndRoleMappingEnabled: this.advancedTeamAndRoleMappingEnabled,
            defaultTeamEnabled: this.defaultTeamEnabled,
            defaultRoleEnabled: this.defaultRoleEnabled,
            additionalGroupDn: this.additionalGroupDn,
            groupObjectClass: this.groupObjectClass,
            groupObjectFilter: this.groupObjectFilter,
            groupNameAttribute: this.groupNameAttribute,
            groupMembersAttribute: this.groupMembersAttribute,
            userMembershipAttribute: this.userMembershipAttribute,
            groupMembersAttributeSource: this.groupMembersAttributeSource      
        };
    }

    getLDAPServerModelByStep(stepIndex) {

        switch (stepIndex) {
            case 0:
                return {
                    active: this.active,
                    password: this.password,
                    name: this.name,
                    host: this.host,
                    port: this.port,
                    username: this.username,
                    useSsl: this.useSsl,
                    verifySslCertificate: this.verifySSLCertificate                          
                };
            case 1:
                const mappedDomainId = this.mappedDomainId == -1 ? null : this.mappedDomainId;

                return {
                    active: this.active,
                    password: this.password,
                    name: this.name,
                    host: this.host,
                    port: this.port,
                    username: this.username,
                    useSsl: this.useSsl,
                    verifySslCertificate: this.verifySSLCertificate, 
                    ldapDirectoryType: this.ldapDirectoryType,
                    ssoEnabled: this.ssoEnabled,
                    mappedDomainId: mappedDomainId,
                    baseDn: this.baseDn,
                    additionalUserDn: this.additionalUserDn,
                    userObjectFilter: this.userObjectFilter,
                    userObjectClass: this.userObjectClass,
                    usernameAttribute: this.usernameAttribute,
                    firstNameAttribute: this.firstNameAttribute,
                    lastNameAttribute: this.lastNameAttribute,
                    emailAttribute: this.emailAttribute                                                                          
                };
            default:
                return this.getLDAPServerModel();
        }
    }
}

export default LDAPStore;


// WEBPACK FOOTER //
// ./src/stores/ldap/ldapStore.js